import React, { ChangeEvent, useState } from "react";
import styles from "./ContactUs.module.css";
import Button from "../components/common/Button";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FAQMailQueryParams } from "../data_provider/gateways/MailingGateway";
import { useModal } from "../components/modal/Modal";
import ModalButtons from "../components/modal/ModalButtons";
import LoadingSpinner from "../components/common/LoadingSpinner";
import DataProvider from "../data_provider/DataProvider";
import { path } from "../components/common/Common";
import { useNavigate } from "react-router";
import { Helmet } from "react-helmet-async";

/**
 * @author mj, smyun
 * @date 2022-09-28, 2025-05-20
 * @remark 지원문의 기술 지원 메일보내기 폼 컴포넌트
 * @returns section
 */

function ContactUs() {
    const [formState, setFormState] = useState<any>({});
    const [errorState, setErrorState] = useState<any>();
    const [isLoading, setIsLoading] = useState(false);
    const { ModalComponent, openModal, closeModal } = useModal();

    const modules = {
        toolbar: [[{ header: [1, 2, false] }], ["bold", "italic", "underline"], ["link"]],
    };
    const formats = ["header", "bold", "italic", "underline", "link"];

    /**
     * input 태그 onChange 이벤트 처리 콜백
     * @param e event
     * @returns void
     */
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setFormState({
            ...formState,
            [e.target.name]: e.target.value,
        });
    };

    /**
     * 텍스트 에디터 onChange 이벤트 처리 콜백
     * @param value 에디터 입력값
     * @returns void
     */
    const editorHandleChange = (content: any, delta: any, source: any, editor: any) => {
        const text = editor.getText().replaceAll("\n", "");
        if (editor.getLength() !== 1 && text !== "") {
            setFormState({
                ...formState,
                desc: editor.getHTML(),
            });
        } else {
            setFormState({
                ...formState,
                desc: "",
            });
        }
    };

    /**
     * 입력값 유효성 검사 콜백
     * @param <none>
     * @returns void
     */
    const validation = () => {
        let alertMsg = {};
        if (formState.name === "" || formState.name === undefined) {
            alertMsg = {
                ...alertMsg,
                name: "이름을 입력해주세요.",
            };
        }
        const emailRule = /^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*.[a-zA-Z]{2,3}$/i;
        if (formState.email === "" || formState.email === undefined) {
            alertMsg = {
                ...alertMsg,
                email: "이메일을 입력해주세요.",
            };
        } else if (!emailRule.test(formState.email)) {
            alertMsg = {
                ...alertMsg,
                email: "이메일 형식이 올바르지 않습니다.",
            };
        }
        if (formState.title === "" || formState.title === undefined) {
            alertMsg = {
                ...alertMsg,
                title: "제목을 입력해주세요.",
            };
        }
        if (formState.desc === "" || formState.desc === undefined) {
            alertMsg = {
                ...alertMsg,
                desc: "상세설명을 입력해주세요.",
            };
        }
        if (Object.keys(alertMsg).length !== 0) {
            setErrorState(alertMsg);
        } else {
            setErrorState({});
            // 메일 API 및 안내모달 | smyun | 25.05.20
            sendMail(formState); // formState 값을 query로 전달
        }
    };

    const showModal = (message: string) => {
        openModal({
            title: "안내",
            children: message,
            onCloseNavigatePath: path.SUPPORT, // 모달창 닫히면 고객지원 페이지로 이동
            modalButtons: (
                <ModalButtons
                    buttonCount={1}
                    buttons={[
                        {
                            label: "확인",
                            color: "Primary",
                            onClick: () => {
                                closeModal();
                            },
                        },
                    ]}
                />
            ),
        });
    };

    const sendMail = async (query: FAQMailQueryParams) => {
        // 로딩 모달 표시
        openModal({
            title: "메일을 발송중입니다...",
            children: <LoadingSpinner />,
        });

        setIsLoading(true);

        try {
            const response = await DataProvider.SendFAQMail(query);
            
            if (response.flag) {
                // 성공 시 결과 모달
                openModal({
                    title: "안내",
                    children: "메일이 발송되었습니다.",
                    onCloseNavigatePath: path.SUPPORT, // 모달창 닫히면 고객지원 페이지로 이동
                    modalButtons: (
                        <ModalButtons
                            buttonCount={1}
                            buttons={[
                                {
                                    label: "확인",
                                    color: "Primary",
                                    onClick: () => {
                                        closeModal();
                                    },
                                },
                            ]}
                        />
                    ),
                });
            } else {
                // 서버 오류 시
                showModal("메일 발송에 실패했습니다. 다시 시도해주세요.");
            }
        } catch (error) {
            // 에러 시 결과 모달
            showModal(error as string || "메일 발송 중 오류가 발생했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>문의하기 - 모두의지도</title>
                <meta name="description" content="무엇이든 편하게 문의해주세요" />
            </Helmet>
            <section className={styles.wrapper}>
                <ModalComponent />
                <div className={styles.tit_bx}>
                    <h2>기술지원팀에게 메일 작성</h2>
                    <p>궁금한 사항을 적어 메일로 보내주시면 빠른 답변 드리겠습니다.</p>
                </div>
                <div className={styles.form_bx}>
                    <div className={styles.form_row}>
                        <h2>이름</h2>
                        <div className={styles.form_col}>
                            <input type="text" className={styles.input_30} name="name" onChange={handleChange} />
                            <p className={styles.alert_msg}>{errorState ? errorState.name : null}</p>
                        </div>
                    </div>
                    <div className={styles.form_row}>
                        <h2>이메일</h2>
                        <div className={styles.form_col}>
                            <input type="text" className={styles.input_50} name="email" onChange={handleChange} />
                            <p className={styles.alert_msg}>{errorState ? errorState.email : null}</p>
                        </div>
                    </div>
                    <div className={styles.form_row}>
                        <h2>제목</h2>
                        <div className={styles.form_col}>
                            <input type="text" className={styles.input_50} name="title" onChange={handleChange} />
                            <p className={styles.alert_msg}>{errorState ? errorState.title : null}</p>
                        </div>
                    </div>
                    <div className={styles.form_row}>
                        <h2>MAP URL</h2>
                        <div className={styles.form_col}>
                            <input type="text" className={styles.input_50} name="url" onChange={handleChange} />
                        </div>
                    </div>
                    <div className={[styles.form_row, styles.editor].join(" ")}>
                        <h2>상세설명</h2>
                        <div className={styles.form_col}>
                            <ReactQuill theme="snow" className={styles.quill} modules={modules} formats={formats} onChange={editorHandleChange} />
                            <p className={styles.alert_msg}>{errorState ? errorState.desc : null}</p>
                        </div>
                    </div>
                </div>
                <div className={styles.btn_bx}>
                    <Button margin={"60px auto 0"} onClick={validation} label="제출" />
                </div>
            </section>
        </>
    );
}

export default ContactUs;
